/**
 * @file     DragDropQuiz.tsx
 * @readme   ../README.md
 * @purpose  Drag-and-drop ordering exercise on the academy kit: 52px rows at radius 16, the success or error tint per row once checked, then one explanation line. Native drag, touch and keyboard reordering; no dependencies.
 */
'use client';

import '@/core/i18n/deferred/academy';
import { useState, useRef, useCallback, useEffect } from 'react';
import Box from '@mui/material/Box';
import { useTranslation } from 'react-i18next';
import { layoutTokens } from '@/design-system/tokens/layout';
import { fontWeightTokens } from '@/design-system/tokens/typography';
import { HubActionButton } from '@/components/hub/HubActionButton';
import { headingFont, hubColors, microLabelSx, reducedMotionSx, transition } from '@/components/hub/hubKitStyles';

export interface DragDropItem {
  id: string;
  label: string;
}

interface DragDropQuizProps {
  prompt: string;
  items: DragDropItem[];
  correctOrder: string[];
  explanation: string;
}

const errorSoft = `color-mix(in srgb, ${hubColors.statusError} 10%, transparent)`;

function shuffle(items: DragDropItem[]): DragDropItem[] {
  const shuffled = [...items];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

const CheckGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

const CrossGlyph = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
    <path d="M7 7l10 10M17 7L7 17" />
  </svg>
);

const GripGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden>
    <circle cx="4" cy="3" r="1.2" />
    <circle cx="10" cy="3" r="1.2" />
    <circle cx="4" cy="7" r="1.2" />
    <circle cx="10" cy="7" r="1.2" />
    <circle cx="4" cy="11" r="1.2" />
    <circle cx="10" cy="11" r="1.2" />
  </svg>
);

export function DragDropQuiz({ prompt, items, correctOrder, explanation }: DragDropQuizProps) {
  const { t } = useTranslation();
  // Server and first client render share the authored order; the shuffle runs
  // after mount so the markup never mismatches on hydration.
  const [order, setOrder] = useState<DragDropItem[]>(items);
  useEffect(() => {
    setOrder(shuffle(items));
  }, [items]);
  const [checked, setChecked] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [selectedForMove, setSelectedForMove] = useState<number | null>(null);
  const [touchActive, setTouchActive] = useState<number | null>(null);
  const touchStartY = useRef<number | null>(null);
  const touchItemIndex = useRef<number | null>(null);
  const touchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isCorrect = checked && order.map((i) => i.id).join(',') === correctOrder.join(',');

  const handleDragStart = (index: number) => {
    setDragIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDrop = (index: number) => {
    if (dragIndex === null || dragIndex === index || checked) return;
    const newOrder = [...order];
    const [moved] = newOrder.splice(dragIndex, 1);
    newOrder.splice(index, 0, moved);
    setOrder(newOrder);
    setDragIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setDragOverIndex(null);
  };

  // Touch support with press delay indicator
  const handleTouchStart = (index: number, e: React.TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchItemIndex.current = index;
    // Show subtle lift after short delay
    touchTimer.current = setTimeout(() => {
      setTouchActive(index);
    }, 150);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (touchItemIndex.current === null || touchStartY.current === null || checked) return;
    // Cancel the lift timer if user moves before delay
    if (touchTimer.current) {
      clearTimeout(touchTimer.current);
      touchTimer.current = null;
    }
    setTouchActive(touchItemIndex.current);
    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY.current;
    const itemHeight = 52;
    const indexDiff = Math.round(diff / itemHeight);
    if (indexDiff !== 0) {
      const fromIndex = touchItemIndex.current;
      const toIndex = Math.max(0, Math.min(order.length - 1, fromIndex + indexDiff));
      if (toIndex !== fromIndex) {
        const newOrder = [...order];
        const [moved] = newOrder.splice(fromIndex, 1);
        newOrder.splice(toIndex, 0, moved);
        setOrder(newOrder);
        touchItemIndex.current = toIndex;
        touchStartY.current = currentY;
      }
    }
  };

  const handleTouchEnd = () => {
    touchItemIndex.current = null;
    touchStartY.current = null;
    setTouchActive(null);
    if (touchTimer.current) {
      clearTimeout(touchTimer.current);
      touchTimer.current = null;
    }
  };

  // Keyboard reordering
  const handleKeyDown = useCallback((e: React.KeyboardEvent, index: number) => {
    if (checked) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setSelectedForMove(selectedForMove === index ? null : index);
    } else if (selectedForMove !== null && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      e.preventDefault();
      const target = e.key === 'ArrowUp'
        ? Math.max(0, selectedForMove - 1)
        : Math.min(order.length - 1, selectedForMove + 1);
      if (target !== selectedForMove) {
        const newOrder = [...order];
        const [moved] = newOrder.splice(selectedForMove, 1);
        newOrder.splice(target, 0, moved);
        setOrder(newOrder);
        setSelectedForMove(target);
        setFocusedIndex(target);
      }
    } else if (e.key === 'Escape') {
      setSelectedForMove(null);
    }
  }, [checked, selectedForMove, order]);

  const handleCheck = () => setChecked(true);

  const handleReset = () => {
    setChecked(false);
    setSelectedForMove(null);
    setOrder(shuffle(items));
  };

  return (
    <Box component="section" aria-label={prompt} sx={{ m: 0 }}>
      <Box component="p" sx={{ ...microLabelSx, mb: 0.75 }}>
        {checked
          ? t('academy.dragDrop.checkedHint', { defaultValue: 'The right order is marked below.' })
          : t('academy.dragDrop.hint', { defaultValue: 'Drag the items into the right order.' })}
      </Box>
      <Box
        component="h3"
        sx={{ m: 0, mb: 2, fontFamily: headingFont, fontSize: { xs: 18, md: 20 }, fontWeight: fontWeightTokens.semibold, lineHeight: 1.3, color: hubColors.textPrimary }}
      >
        {prompt}
      </Box>

      <Box role="list" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {order.map((item, index) => {
          const isItemCorrect = checked && item.id === correctOrder[index];
          const isItemWrong = checked && item.id !== correctOrder[index];
          const isDragging = dragIndex === index;
          const isDragOver = dragOverIndex === index;
          const isTouchLifted = touchActive === index;
          const isSelectedForMove = selectedForMove === index;
          const accent = isSelectedForMove || isDragOver || isTouchLifted;

          return (
            <Box
              key={item.id}
              tabIndex={checked ? -1 : 0}
              role="listitem"
              aria-label={t('academy.dragDrop.itemLabel', {
                defaultValue: '{{label}}, position {{n}}',
                label: item.label,
                n: index + 1,
              }) + (isSelectedForMove ? t('academy.dragDrop.itemSelected', { defaultValue: ', selected, use the arrow keys' }) : '')}
              draggable={!checked}
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={() => handleDrop(index)}
              onDragEnd={handleDragEnd}
              onTouchStart={(e) => handleTouchStart(index, e)}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onKeyDown={(e) => handleKeyDown(e, index)}
              onFocus={() => setFocusedIndex(index)}
              onBlur={() => setFocusedIndex(null)}
              sx={{
                boxSizing: 'border-box',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                minHeight: 52,
                px: 2,
                py: 1.25,
                borderRadius: `${layoutTokens.radius.md}px`,
                border: `1px solid ${
                  isItemCorrect ? hubColors.statusSuccess
                  : isItemWrong ? hubColors.statusError
                  : accent ? hubColors.interactivePrimary
                  : hubColors.borderStrong
                }`,
                backgroundColor: isItemCorrect ? hubColors.successSoft : isItemWrong ? errorSoft : hubColors.surfaceCard,
                color: hubColors.textPrimary,
                opacity: isDragging ? 0.5 : 1,
                cursor: checked ? 'default' : 'grab',
                userSelect: 'none',
                touchAction: 'none',
                transition: transition('border-color', 'background-color', 'opacity'),
                '&:active': checked ? {} : { cursor: 'grabbing' },
                '&:focus-visible': { outline: `2px solid ${hubColors.interactiveFocusRing}`, outlineOffset: 2 },
                '& svg': { flexShrink: 0 },
                ...reducedMotionSx,
              }}
            >
              <Box
                component="span"
                sx={{ width: 20, flexShrink: 0, textAlign: 'center', fontSize: 14, fontWeight: fontWeightTokens.medium, color: hubColors.textMuted, fontVariantNumeric: 'tabular-nums' }}
              >
                {index + 1}
              </Box>
              <Box component="span" sx={{ flex: 1, fontSize: 15, lineHeight: 1.4 }}>
                {item.label}
              </Box>
              <Box
                component="span"
                sx={{ display: 'flex', color: isItemCorrect ? hubColors.statusSuccess : isItemWrong ? hubColors.statusError : hubColors.textMuted }}
              >
                {isItemCorrect ? <CheckGlyph /> : isItemWrong ? <CrossGlyph /> : <GripGlyph />}
              </Box>
            </Box>
          );
        })}
      </Box>

      {checked ? (
        <Box component="p" role="status" sx={{ m: 0, mt: 1.5, fontSize: 14, lineHeight: 1.55, color: hubColors.textSecondary }}>
          <Box component="span" sx={{ fontWeight: fontWeightTokens.medium, color: isCorrect ? hubColors.statusSuccess : hubColors.statusError }}>
            {isCorrect
              ? t('academy.dragDrop.right', { defaultValue: 'Right order.' })
              : t('academy.dragDrop.notQuite', { defaultValue: 'Not quite.' })}
          </Box>{' '}
          {explanation}
        </Box>
      ) : null}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        {checked ? (
          <HubActionButton variant="secondary" action={{ label: t('academy.dragDrop.retry', { defaultValue: 'Try again' }), onClick: handleReset }} />
        ) : (
          <HubActionButton action={{ label: t('academy.dragDrop.check', { defaultValue: 'Check order' }), onClick: handleCheck }} />
        )}
      </Box>
    </Box>
  );
}
